import { useCallback } from 'react'
import type { StreamConnectionError, StreamType } from '@/infra'
import { createParseError } from '@/infra'

export interface SSEChunk {
  readonly event?: string
  readonly data?: string
}

export type ParseResult<T> =
  | { success: true; payload: T }
  | { success: false; error: StreamConnectionError }

export interface ConnectionConfirmation {
  readonly connectionId: string
  readonly message: string
}

export function parseSSEChunk(chunk: string): SSEChunk | null {
  const lines = chunk.split('\n')
  const result: { event?: string; data?: string } = {}

  for (const line of lines) {
    if (line.startsWith('event:')) {
      result.event = line.substring(6).trim()
    } else if (line.startsWith('data:')) {
      result.data = line.substring(5).trim()
    }
  }

  return result.data ? result : null
}

export function isConnectionConfirmation(obj: unknown): obj is ConnectionConfirmation {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  const candidate = obj as Record<string, unknown>
  return typeof candidate.connectionId === 'string' && typeof candidate.message === 'string'
}

export function parseJSON<T>(
  data: string,
  streamType: StreamType,
  validator: (obj: unknown) => obj is T,
): ParseResult<T> {
  try {
    const parsed: unknown = JSON.parse(data)
    if (validator(parsed)) {
      return { success: true, payload: parsed }
    }
    return {
      success: false,
      error: createParseError('Invalid payload structure', streamType, data),
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown parse error'
    return {
      success: false,
      error: createParseError(message, streamType, data),
    }
  }
}

export function parseConnectionConfirmation(
  data: string,
  streamType: StreamType,
): ParseResult<ConnectionConfirmation> {
  return parseJSON(data, streamType, isConnectionConfirmation)
}

export interface StreamParserCallbacks<TPayload> {
  readonly onEvent: (payload: TPayload) => void
  readonly onConnection: (confirmation: ConnectionConfirmation) => void
  readonly onError: (error: StreamConnectionError) => void
}

function processEventMessage<TPayload>(
  data: string,
  streamType: StreamType,
  payloadValidator: (obj: unknown) => obj is TPayload,
  onEvent: (payload: TPayload) => void,
  onError: (error: StreamConnectionError) => void,
): void {
  const result = parseJSON(data, streamType, payloadValidator)
  if (result.success) {
    onEvent(result.payload)
  } else {
    onError(result.error)
  }
}

function processConnectionMessage(
  data: string,
  streamType: StreamType,
  onConnection: (confirmation: ConnectionConfirmation) => void,
  onError: (error: StreamConnectionError) => void,
): void {
  const result = parseConnectionConfirmation(data, streamType)
  if (result.success) {
    onConnection(result.payload)
  } else {
    onError(result.error)
  }
}

export function useStreamParser<TPayload>(
  streamType: StreamType,
  eventNames: readonly string[],
  connectionEventName: string,
  payloadValidator: (obj: unknown) => obj is TPayload,
  callbacks: StreamParserCallbacks<TPayload>,
): (buffer: string) => string {
  const { onEvent, onConnection, onError } = callbacks

  return useCallback(
    (buffer: string): string => {
      const messages = buffer.split('\n\n')
      const remainingBuffer = messages.pop() ?? ''

      for (const message of messages) {
        if (!message.trim()) continue

        const sseChunk = parseSSEChunk(message)
        if (!sseChunk?.data) continue

        if (eventNames.includes(sseChunk.event ?? '')) {
          processEventMessage(sseChunk.data, streamType, payloadValidator, onEvent, onError)
        } else if (sseChunk.event === connectionEventName) {
          processConnectionMessage(sseChunk.data, streamType, onConnection, onError)
        }
      }

      return remainingBuffer
    },
    [streamType, eventNames, connectionEventName, payloadValidator, onEvent, onConnection, onError],
  )
}
